import { PurchaseError } from "@b2bportal/purchase-api";
import {
  CardPaymentEventTypes,
  CartFulfillEventType,
  cartFulfillSelectors,
  CartQuoteEventType,
  cartQuoteSelectors,
  getParentState,
  ParentState,
  useCheckoutState,
  useCheckoutStateSelector,
} from "@capone/checkout";
import { getConfigTenant, isCorpTenant, isVesperTenant, PATH_HOME } from "@capone/common";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  GenericInfoPopup,
  Icon,
  IconName,
  useDeviceTypes,
  usePrevious,
} from "halifax";
import React, { useEffect } from "react";
import { RouteComponentProps } from "react-router";
import {
  CallState,
  MODAL_ALERT,
  ModalAlertProperties,
  ModalCategoryType,
  ModalScreens,
  REDIRECT_TO_PACKAGE_HOTEL_SHOP,
} from "redmond";
import { trackEvent } from "../../../../api/v0/trackEvent";
import {
  goToAvailability,
  goToFlightShop,
  goToHotelShop,
} from "../../../hotel-shop/utils/queryStringHelpers";
import { Event, TEvent } from "../../state/events";
import {
  getFinalizePackageCallState,
  getFinalizePackageResponse,
} from "../../state/selectors/common";
import { PackagesMachineContext } from "../../state/types";
import { BookingErrorModalConnectorProps } from "./container";
import "./styles.scss";
import { getErrorModalProps } from "./utils";
import { useCorePackagesNavigation } from "../../../../context/CorePackagesNavigationContext";

export interface IBookingErrorModalProps
  extends BookingErrorModalConnectorProps,
    RouteComponentProps {}

export const BookingErrorModal = ({
  history,
  origin,
  departureDate,
  returnDate,
  selectedLodging,
  stopsOption,
  travelers,
  fareClassOptionFilter,
}: IBookingErrorModalProps) => {
  const { matchesMobile } = useDeviceTypes();
  const coreNav = useCorePackagesNavigation();

  const quoteErrorOpen = useCheckoutStateSelector(
    cartQuoteSelectors.getOpenCartQuoteErrorModal
  );
  const quoteError = useCheckoutStateSelector(
    cartQuoteSelectors.getCartQuoteError
  );
  const fulfillErrorOpen = useCheckoutStateSelector(
    cartFulfillSelectors.getOpenCartFulfillErrorModal
  );
  const fulfillError = useCheckoutStateSelector(
    cartFulfillSelectors.getCartFulfillError
  );

  const priceChange = useCheckoutStateSelector(
    cartQuoteSelectors.getCartQuotePriceChange
  );

  const [pqExpired, setPQExpired] = React.useState(false);

  const quoteTimeSucceeded = useCheckoutStateSelector(
    cartQuoteSelectors.getCartQuoteTimeSucceeded
  );

  const isCorporate = isCorpTenant(getConfigTenant());
  const isVesper = isVesperTenant(getConfigTenant());

  //  callstate of the finalize package service (used on checkout in control if finalize failed on review itinerary and isn't caught OR on available variant for desktop)
  const finalizePackageCallState = useCheckoutStateSelector(
    getFinalizePackageCallState
  );
  const finalizeCallFailed = finalizePackageCallState === CallState.Failed;
  const finalizePackageResponse = useCheckoutStateSelector(
    getFinalizePackageResponse
  );

  useEffect(() => {
    if (finalizeCallFailed) {
      trackEvent({
        eventName: REDIRECT_TO_PACKAGE_HOTEL_SHOP,
        properties: {
          screen: "package_checkout",
          reason: "missing_package_finalize_response",
        },
        encryptedProperties: [],
      });
    }
  }, [finalizeCallFailed]);

  React.useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | number | null = null;

    if (!quoteTimeSucceeded) {
      setPQExpired(false);
    } else {
      const timeUntilExpiringQuote = dayjs(quoteTimeSucceeded)
        .add(window.__mclean_env__.PACKAGE_PQ_TIMEOUT ?? 15, "minutes")
        .diff();

      timeout = setTimeout(() => {
        setPQExpired(true);
      }, timeUntilExpiringQuote);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [quoteTimeSucceeded]);

  const open =
    finalizeCallFailed ||
    pqExpired ||
    fulfillErrorOpen ||
    quoteErrorOpen ||
    !!(priceChange?.hasDifference && !priceChange.acknowledged);

  const error = fulfillError || quoteError;

  const previousError = usePrevious(error);
  const previousQuoteError = usePrevious(quoteError);
  const previousFulfillError = usePrevious(fulfillError);

  const [state, send] = useCheckoutState<TEvent, PackagesMachineContext>();

  const parentState = getParentState(state.value) as ParentState;

  const clearError = () => {
    send(CartQuoteEventType.CLEAR_CART_QUOTE_ERROR);
    send(CartFulfillEventType.CLEAR_CART_FULFILL_ERROR);
  };

  const updateFlightTravelers = () => {
    send(CartQuoteEventType.OPEN_PASSENGER_PICKER);
    clearError();
  };

  const updateHotelTravelers = () => {
    send(Event.GO_TO_HOTEL_PASSENGER_SELECT);
    clearError();
  };

  const handleAcknowledgePriceChange = () => {
    send(CardPaymentEventTypes.ACKNOWLEDGE_PRICE_CHANGE);
  };

  const handleRetryQuote = () => {
    send(CartQuoteEventType.RETRY_QUOTE);
  };

  const handleGoToAvailability = () => {
    if (coreNav.goToHotelAvailability != null) {
      coreNav.goToHotelAvailability();
    } else {
      goToAvailability({
        history,
        lodging: selectedLodging,
        fromDate: departureDate,
        untilDate: returnDate,
        origin,
        adultsCount: travelers.adultsCount,
        children: travelers.children,
        infants: travelers.infants,
        stopsOption,
        fareClassOptionFilter,
      });
    }
  };

  const handleGoToFlightShop = () => {
    if (coreNav.goToFlightShopOutbound != null) {
      coreNav.goToFlightShopOutbound();
    } else {
      goToFlightShop({ history });
    }
  };

  const handleGoHome = () => {
    history.push(PATH_HOME);
  };

  const handleGoToHotelShop = () => {
    if (coreNav.goToHotelShop != null) {
      coreNav.goToHotelShop();
    } else {
      goToHotelShop({ history });
    }
  };

  const errorModalProps = getErrorModalProps(
    error?.type || previousError?.type, // fallback to previous error state to prevent flashing of default error on modal close
    (error?.data || previousError?.data) as PurchaseError[],
    priceChange,
    finalizePackageResponse?.requoteReason,
    !!(quoteError || previousQuoteError),
    !!(fulfillError || previousFulfillError),
    updateFlightTravelers,
    updateHotelTravelers,
    handleAcknowledgePriceChange,
    handleRetryQuote,
    handleGoToAvailability,
    handleGoToFlightShop,
    pqExpired,
    finalizeCallFailed,
    handleGoHome,
    handleGoToHotelShop,
  );

  const modalEventProperties: ModalAlertProperties = {
    type: error?.type || "",
    primary_button:
      errorModalProps.buttons?.[0]?.buttonText?.replaceAll(" ", "_") || "",
    secondary_button:
      errorModalProps.buttons?.[1]?.buttonText.replaceAll(" ", "_") || "",
    screen: ModalScreens.PACKAGES_CHECKOUT,
    category: ModalCategoryType.TROUBLE,
    modal_subtitle: errorModalProps.subtitle as string,
    modal_title: errorModalProps.title as string,
    agent_subtitle: errorModalProps.subtitle as string,
    agent_title: errorModalProps.title as string,
    funnel: "packages",
    step: parentState,
  };

  React.useEffect(() => {
    if (open)
      trackEvent({
        eventName: MODAL_ALERT,
        properties: {
          ...modalEventProperties,
        },
      });
  }, [open]);

  return (
    <GenericInfoPopup
      open={open}
      image={
        <Icon
          className="error-icon"
          name={isCorporate ? IconName.Caution : IconName.ErrorState}
        />
      }
      className={clsx("packages-book-error-modal", {
        mobile: matchesMobile,
        noIconPadding: isCorporate,
        redesigned: isVesper,
      })}
      zIndex={1301}
      {...errorModalProps}
    />
  );
};
